// AcademicYearsList.jsx (исправленная версия)
import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faEllipsisVertical,
  faPlus,
  faEdit,
  faArchive,
  faBoxOpen,
  faTrash,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import { Alert } from 'react-bootstrap';
import AcademicYearModal from './AcademicYearModal';
import { useReference } from '../actions/useReference';
import '../../QueueFull/cabinet-zdo-requests.css';
import '../TypeDocuments/style.css';

const AcademicYearsList = ({ isEditPossible = true }) => {
  const [activeTab, setActiveTab] = useState('active');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeMenuIndex, setActiveMenuIndex] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);

  const { 
    data, 
    config, 
    loading, 
    error,
    actions 
  } = useReference('academic-years');

  useEffect(() => {
    actions.reload(activeTab === 'active' ? 'active' : 'archived');
  }, [activeTab]);

  // Функция для получения данных из additionalFields
  const getItemData = (item) => {
    if (!item) return {};
    
    return {
      id: item._id,
      name: item.name,
      yearLabel: item.name, // Используем name как yearLabel
      year: item.additionalFields?.year || '',
      groupFormationStart: item.additionalFields?.groupFormationStart || '',
      publicDisplayStart: item.additionalFields?.publicDisplayStart || '',
      invitationStart: item.additionalFields?.invitationStart || '',
      childTransferStart: item.additionalFields?.childTransferStart || '',
      createdAtFormatted: item.createdAtFormatted,
      updatedAtFormatted: item.updatedAtFormatted,
      status: item.status,
      _id: item._id
    };
  };

  // Фільтрація даних за статусом
  const filteredData = useMemo(() => {
    return data.filter(item => item.status === activeTab);
  }, [data, activeTab]);

  // Преобразуем данные для отображения
  const displayData = useMemo(() => {
    return filteredData.map(item => getItemData(item));
  }, [filteredData]);

  const columns = [
    { key: 'yearLabel', label: 'Навчальний рік', sortable: true, width: '15%' },
    { key: 'groupFormationStart', label: 'Дата початку формування груп', sortable: true, width: '15%' },
    { key: 'publicDisplayStart', label: 'Дата публічного показу груп', sortable: true, width: '15%' },
    { key: 'invitationStart', label: 'Дата початку розсилки запрошень', sortable: true, width: '15%' },
    { key: 'childTransferStart', label: 'Дата початку переведення дітей', sortable: true, width: '15%' },
    { key: 'createdAtFormatted', label: 'Дата створення', sortable: true, width: '10%' },
    { key: 'updatedAtFormatted', label: 'Дата редагування', sortable: true, width: '10%' },
    ...(isEditPossible ? [{ key: 'actions', label: 'Дії', sortable: false, width: '5%' }] : [])
  ];

  // Сортування
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return displayData;
    
    return [...displayData].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [displayData, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const handleMenuToggle = (index) => {
    setActiveMenuIndex(activeMenuIndex === index ? null : index);
  };

  const handleMenuClose = () => {
    setActiveMenuIndex(null);
  };

  const handleActionSelect = async (actionId, item) => {
    try {
      if (actionId === 'edit') {
        setSelectedItem(item);
        setViewMode(false);
        setIsModalOpen(true);
      } else if (actionId === 'view') {
        setSelectedItem(item);
        setViewMode(true);
        setIsModalOpen(true);
      } else if (actionId === 'archive') {
        await actions.archive(item._id);
      } else if (actionId === 'restore') {
        await actions.restore(item._id);
      } else if (actionId === 'delete') {
        if (window.confirm(`Ви впевнені, що хочете назавжди видалити "${item.yearLabel}"?`)) {
          await actions.delete(item._id);
        }
      }
      actions.reload(activeTab === 'active' ? 'active' : 'archived');
    } catch (error) {
      console.error('Помилка при виконанні дії:', error);
      alert(error.message);
    } finally {
      setActiveMenuIndex(null);
    }
  };

  const handleAddItem = () => {
    if (!isEditPossible) return;
    setSelectedItem(null);
    setViewMode(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setViewMode(false);
  };

  const handleSubmit = async (formData) => {
    try {
      if (selectedItem) {
        await actions.update(selectedItem._id, formData);
      } else {
        await actions.create(formData);
      }
      actions.reload(activeTab === 'active' ? 'active' : 'archived');
      handleCloseModal();
    } catch (error) {
      console.error('Помилка при збереженні:', error);
      alert('Помилка при збереженні: ' + error.message);
    }
  };

  // Функция для преобразования данных для модального окна
  const transformItemData = (item) => {
    if (!item) return null;
    
    return {
      name: item.name,
      year: item.year,
      groupFormationStart: item.groupFormationStart,
      publicDisplayStart: item.publicDisplayStart,
      invitationStart: item.invitationStart,
      childTransferStart: item.childTransferStart
    };
  };

  const ActionsMenu = ({ onActionSelect, onClose, item, isArchived }) => {
    const baseActions = [
      { id: 'view', label: 'Переглянути', icon: faEye }
    ];

    const editActions = isArchived 
      ? [
          { id: 'edit', label: 'Редагувати', icon: faEdit },
          { id: 'restore', label: 'Поновити з архіву', icon: faBoxOpen },
          { id: 'delete', label: 'Видалити', icon: faTrash }
        ]
      : [
          { id: 'edit', label: 'Редагувати', icon: faEdit },
          { id: 'archive', label: 'Відправити до архіву', icon: faArchive }
        ];

    const actions = isEditPossible 
      ? [...baseActions, ...editActions]
      : baseActions;

    return (
      <div className="actions-dropdown">
        {actions.map((action) => (
          <button
            key={action.id}
            className="dropdown-item d-flex align-items-center gap-2 p-2"
            onClick={() => onActionSelect(action.id, item)}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              width: '100%',
              textAlign: 'left',
              fontSize: '14px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <FontAwesomeIcon 
              icon={action.icon} 
              style={{ 
                width: '16px', 
                color: action.id === 'delete' ? '#dc3545' : '#6c757d' 
              }} 
            />
            <span style={{ 
              color: action.id === 'delete' ? '#dc3545' : '#212529' 
            }}>
              {action.label}
            </span>
          </button>
        ))}
        
        <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
          <button
            className="btn btn-sm btn-outline-secondary w-100"
            onClick={onClose}
            style={{ fontSize: '12px' }}
          >
            Скасувати
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <main className="queuetable-main-content queuetable-page">
        <div className="container-fluid">
          <div className="queuetable-content-wrapper">
            {/* Панель табів */}
            <div className="queuetable-year-bar d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="queuetable-label text-white-50">Навчальний  рік</span>
                <ul className="queuetable-year-tabs nav nav-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'active' ? 'active' : ''}`}
                      onClick={() => setActiveTab('active')}
                    >
                      Актуальні
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'archived' ? 'active' : ''}`}
                      onClick={() => setActiveTab('archived')}
                    >
                      Архівні
                    </button>
                  </li>
                </ul>
              </div>
              {isEditPossible && (
                <div className="queuetable-switch-wrap">
                  <a 
                    href="" 
                    className="link-primary small text-nowrap" 
                    onClick={(e) => { e.preventDefault(); handleAddItem(); }}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Додати навчальний рік
                  </a>
                </div>
              )}
            </div>

            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            {/* Таблиця */}
            <div className="card queuetable-table-card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table queuetable-table align-middle mb-0">
                    <thead>
                      <tr>
                        {columns.map((column) => (
                          <th 
                            key={column.key}
                            style={{ width: column.width }}
                            onClick={() => column.sortable && handleSort(column.key)}
                          >
                            <div className="d-flex align-items-center">
                              {column.label}
                              {column.sortable && (
                                <FontAwesomeIcon 
                                  icon={getSortIcon(column.key)} 
                                  className="ms-1" 
                                  style={{ fontSize: '12px' }}
                                />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={columns.length} className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Завантаження...</span>
                            </div>
                            <p className="mt-2 mb-0">Завантаження навчальних років...</p>
                          </td>
                        </tr>
                      ) : sortedData.length > 0 ? (
                        sortedData.map((item, index) => (
                          <tr key={item._id}>
                            <td>{item.yearLabel}</td>
                            <td>{item.groupFormationStart}</td>
                            <td>{item.publicDisplayStart}</td>
                            <td>{item.invitationStart}</td>
                            <td>{item.childTransferStart}</td>
                            <td>{item.createdAtFormatted}</td>
                            <td>{item.updatedAtFormatted}</td>
                            {isEditPossible && (
                              <td className="queuetable-actions text-end">
                                <div className="actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
                                  <button
                                    className="btn btn-sm btn-link text-decoration-none p-0"
                                    onClick={() => handleMenuToggle(index)}
                                    style={{ color: '#0069ff' }}
                                    disabled={loading}
                                  >
                                    <FontAwesomeIcon icon={faEllipsisVertical} />
                                  </button>
                                  
                                  {activeMenuIndex === index && (
                                    <ActionsMenu 
                                      onActionSelect={handleActionSelect}
                                      onClose={handleMenuClose}
                                      item={item}
                                      isArchived={activeTab === 'archived'}
                                    />
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={columns.length} className="text-center py-4">
                            <div className="text-muted">
                              <p className="mb-1">Навчальний  рік не знайдено</p>
                              <small>Спробуйте змінити вкладку або додати новий навчальний рік</small>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Пагінація */}
                <div className="d-flex align-items-center mt-4 queuetable-pagination-wrapper">
                  <div className="queuetable-pagination-info">
                    Всього {sortedData.length} навчальних років
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Используем наш кастомный AcademicYearModal */}
      <AcademicYearModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        academicYearData={transformItemData(selectedItem)}
        onSubmit={handleSubmit}
        viewMode={viewMode}
        isEditPossible={isEditPossible}
      />
    </>
  );
};

export default AcademicYearsList;